import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, wanneer, getal } from '../i18n.js';
import { St, Spinner } from './Status.jsx';

/**
 * De syslog-standaard bij een FTD onder een FMC (docs/besluiten/syslog-standaard-uitrollen.md): hoe hij
 * syslog stuurt, wat er op de FMC staat naast de standaard (per veld, met de naam in de API), uitrollen
 * met een bevestiging, en de uitrollen met hun stappen en terugdraaien.
 *
 * Vergelijken leest live van de FMC en duurt een paar seconden: alleen op verzoek, niet elke 10 seconden.
 */
const VELD = {
  enableLogging: 'Logging aan',
  facility: 'Facility',
  deviceIdConfigType: 'Device-ID in elk bericht',
  niveau: 'Niveau naar de syslogservers',
  allowUserTrafficWhenDown: 'Verkeer doorlaten als de syslogserver (TCP) wegvalt',
  protocol: 'Protocol',
  port: 'Poort',
  isMgmtReachable: 'Via de management-interface',
  interfaces: 'Via de zones',
  logMessagesInEMBLEM: 'EMBLEM-formaat',
  secureSyslog: 'Secure syslog (TLS)',
  enableipsSyslog: 'IPS-events naar syslog',
  ipsSyslogSeverity: 'Ernst van IPS-events',
  enableFileAndMalwareSyslog: 'File- en malware-events naar syslog',
  fileAndMalwareSyslogSeverity: 'Ernst van file- en malware-events',
  syslogConfigFromPlatformSetting: 'Syslogservers uit het platformbeleid',
  severityForPlatformSettingSyslogConfig: 'Ernst via het platformbeleid',
  enableSyslog: 'Standaardactie (implicit deny) naar syslog',
};
const apiObject = { basic: 'syslog/basicloggingsetups', syslogsettings: 'syslog/syslogsettings', bestemming: 'syslog/loggingdestinations',
  servers: 'syslog/servers', server: 'syslog/servers', logging: 'loggingsettings', implicitdeny: 'defaultactions', regel: 'accessrules' };
const STAND = { wacht: 'wacht', uitrollen: 'bezig met uitrollen', nakijken: 'nakijken', klaar: 'klaar',
  onvolledig: 'uitgerold, maar er komt niets binnen', gestopt: 'gestopt', mislukt: 'mislukt' };
const SOORT = { klaar: 'good', nakijken: 'neutral', uitrollen: 'neutral', wacht: 'neutral', gestopt: 'warning', onvolledig: 'serious', mislukt: 'critical' };
/* Nog bezig: deze statussen lopen nog; de pagina ververst elke tien seconden, dus ze schuiven vanzelf door. */
const BEZIG = new Set(['wacht', 'uitrollen', 'nakijken']);
/* Wat elke status betekent, in één zin (tooltip op de badge, en zichtbaar bij een lopende uitrol). */
const UITLEG = {
  wacht: 'In de wachtrij; de planner begint zo met uitrollen.',
  uitrollen: 'ADTS ZERO schrijft de wijzigingen naar de FMC en laat ze uitrollen (deploy).',
  nakijken: 'Uitgerold; ADTS ZERO wacht tot er weer syslog van de firewall binnenkomt (tot 15 minuten), als controle dat de juiste berichten echt aankomen.',
  klaar: 'Afgerond: de firewall stuurt de syslog zoals bedoeld.',
  onvolledig: 'Uitgerold, maar binnen 15 minuten kwam er geen syslog binnen — controleer de route en het pad naar de sensor. Komt er later alsnog syslog binnen, dan zet ADTS ZERO dit vanzelf op klaar.',
  gestopt: 'Niet uitgevoerd omdat er iets in de weg stond; er is niets op de FMC gewijzigd.',
  mislukt: 'Er ging iets mis bij het schrijven of uitrollen; ADTS ZERO heeft teruggedraaid wat al gewijzigd was.',
};

const waarde = (v) => {
  /* Een block-regel zet logBegin én enableSyslog samen: toon dat als één 'aan'/'uit'. */
  if (v && typeof v === 'object' && 'enableSyslog' in v) return v.logBegin && v.enableSyslog ? t('aan') : t('uit');
  return v === true ? t('aan') : v === false ? t('uit') : v === null || v === undefined || v === '' ? '—' : String(v);
};
const label = (w) => {
  if (w.object === 'regel') return t('Block-regel {naam} naar syslog', { naam: w.regel });
  return w.veld === 'aanmaken' ? (w.object === 'server' ? t('Server {ip} toevoegen', { ip: w.ip }) : t('Bestemming syslogservers toevoegen'))
    : `${t(VELD[w.veld] ?? w.veld)}${w.object === 'server' ? ` (${w.ip})` : ''}`;
};

export default function SyslogStandaard({ bron, mij }) {
  const client = useQueryClient();
  const magBeheren = mij.permissions.includes('bronnen.beheren');
  const magUitrollen = mij.permissions.includes('bronnen.uitrollen');
  const [vraag, zetVraag] = useState(false);
  const q = useQuery({ queryKey: ['syslog-standaard', bron.id], queryFn: () => api.syslogStandaard(bron.id), enabled: vraag && magBeheren, retry: false });
  const [fout, zetFout] = useState(null);
  const [bevestig, zetBevestig] = useState(false);
  const [ookAndere, zetOokAndere] = useState(false);
  const [onderbreking, zetOnderbreking] = useState(false);
  const [route, zetRoute] = useState(null);
  /* Zolang een vraag loopt, kan er niet nog een: twee sessies vlak na elkaar gaven op FMC011 een fout. */
  const [bezig, zetBezig] = useState(false);
  const d = q.data?.data;
  const actief = bron.syslogRollouts.find((r) => ['wacht', 'uitrollen', 'nakijken'].includes(r.status));

  const doe = async (fn) => {
    if (bezig) return;
    zetFout(null); zetBezig(true);
    try { await fn(); } catch (e) { zetFout(t(e.details ? Object.values(e.details)[0] : e.message)); } finally { zetBezig(false); }
  };
  const bewaarRoute = (r) => doe(async () => {
    await api.zetSyslogRoute(bron.id, r);
    zetRoute(null);
    await client.invalidateQueries({ queryKey: ['bron'] });
    if (vraag) await q.refetch(); else zetVraag(true);
  });
  const rolUit = () => doe(async () => {
    await api.rolSyslogUit(bron.id, d.hash, ookAndere, onderbreking);
    zetBevestig(false);
    await client.invalidateQueries({ queryKey: ['bron'] });
  });
  const terug = (rid) => doe(async () => { await api.draaiSyslogTerug(bron.id, rid); await client.invalidateQueries({ queryKey: ['bron'] }); });
  const anderen = d?.devices?.filter((x) => x.id !== d.device?.id) ?? [];
  /* De HA-partner gaat altijd automatisch mee; alleen níet-HA firewalls die hetzelfde beleid delen vragen om een vinkje. */
  const haIds = new Set((d?.haPartners ?? []).map((x) => x.id));
  const haPartners = anderen.filter((x) => haIds.has(x.id));
  const nietHa = anderen.filter((x) => !haIds.has(x.id));
  /* Een ander beleid toewijzen (beleid-op-de-fmc): kiezen, de naam van de firewall overtypen, en bij een
     toegangsbeleid de onderbreking bevestigen. */
  const [toewijs, zetToewijs] = useState(null);
  const toegangAnders = toewijs && toewijs.toegangId && toewijs.toegangId !== d?.access?.id;
  const platformAnders = toewijs && toewijs.platformId && toewijs.platformId !== d?.platform?.id;
  const wijsToe = () => doe(async () => {
    await api.wijsBeleidToe(bron.id, { platformId: platformAnders ? toewijs.platformId : null, toegangId: toegangAnders ? toewijs.toegangId : null,
      bevestig: toewijs.bevestig.trim(), onderbrekingMag: toewijs.onderbreking, reden: toewijs.reden.trim() || null });
    zetToewijs(null);
    await client.invalidateQueries({ queryKey: ['bron'] });
  });
  const nuRoute = route ?? bron.syslogRoute ?? '';

  return (
    <section className="card">
      <header><h2>{t('Syslog-standaard')}</h2>
        {magBeheren && <span className="meta"><button type="button" className="namelink" disabled={q.isFetching || bezig}
          onClick={() => (vraag ? q.refetch() : zetVraag(true))}>{q.isFetching ? t('Bezig met controleren…') : t('Controleren via de FMC')}</button></span>}</header>
      {fout && <div className="melder rood" style={{ marginBottom: 10 }}>{fout}</div>}

      <dl className="facts">
        <dt>{t('Syslog via')}</dt>
        <dd>{magBeheren ? (
          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={nuRoute.startsWith('zone:') ? 'zone' : nuRoute} onChange={(e) => zetRoute(e.target.value === 'zone' ? `zone:${(d?.zones ?? [])[0] ?? ''}` : e.target.value)}>
              {!bron.syslogRoute && <option value="">{t('Kies…')}</option>}
              <option value="management">{t('de management-interface')}</option>
              <option value="zone">{t('een data-zone')}</option>
            </select>
            {nuRoute.startsWith('zone:') && (d?.zones?.length
              ? <select value={nuRoute.slice(5)} onChange={(e) => zetRoute(`zone:${e.target.value}`)}>{d.zones.map((z) => <option key={z} value={z}>{z}</option>)}</select>
              : <span className="mono">{nuRoute.slice(5) || t('vergelijk eerst om de zones te zien')}</span>)}
            {route && route !== bron.syslogRoute && /^(management|zone:.+)$/.test(route) && <button type="button" className="btn" onClick={() => bewaarRoute(route)}>{t('Opslaan')}</button>}
          </span>
        ) : (bron.syslogRoute ? (bron.syslogRoute === 'management' ? t('de management-interface') : bron.syslogRoute.slice(5)) : '—')}</dd>
        {d?.platform && <><dt>{t('Platformbeleid')}</dt><dd className="mono">{d.platform.name} <span className="muted">· {d.platform.devices.map((x) => x.name).join(', ')}</span></dd></>}
        {d?.access && <><dt>{t('Toegangsbeleid')}</dt><dd className="mono">{d.access.name} <span className="muted">· {d.access.devices.map((x) => x.name).join(', ')}</span></dd></>}
        {d?.ontvangst && <><dt>{t('Komt binnen (24 u)')}</dt>
          <dd>{d.ontvangst.totaal === 0
            ? <St soort="serious">{t('ADTS ZERO ontvangt geen syslog van deze firewall')}</St>
            : <span style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{[['VPN-logins', 'vpn'], ['geweigerd', 'weigering'], ['verbindingen', 'verbinding'], ['IPS', 'ips'], ['beheer', 'beheer']].map(([lbl, k]) => (
                <span key={k} className={d.ontvangst[k] > 0 ? '' : 'muted'}>{d.ontvangst[k] > 0 ? '✓' : '–'} {t(lbl)}{d.ontvangst[k] > 0 ? ` (${getal(d.ontvangst[k])})` : ''}</span>
              ))}</span>}</dd></>}
      </dl>

      {q.isError && <div className="melder rood" style={{ marginTop: 10 }}>{t(q.error.message)}</div>}
      {d && !d.available && <p className="empty">{t(d.reason)}</p>}
      {d?.available && (
        <>
          {d.objections.map((b) => <div key={b} className="callout warn" style={{ marginTop: 10 }}>{t(b)}</div>)}
          {d.pending.length > 0 && <div className="callout warn" style={{ marginTop: 10 }}>{t('Op de FMC staat al iets klaar voor: {namen}. ADTS ZERO rolt pas uit als dat eerst is uitgerold of teruggedraaid.', { namen: d.pending.map((x) => x.name).join(', ') })}</div>}
          {!d.objections.length && (!bron.syslogRoute
            ? <p style={{ marginTop: 10 }}><St soort="warning">{t('Kies eerst hoe de syslog verstuurt; dan controleert ADTS ZERO of de instellingen goed staan.')}</St></p>
            : d.changes.length === 0
            ? <p style={{ marginTop: 10 }}><St soort="good">{t('De syslog-instellingen staan goed voor ADTS ZERO.')}</St></p>
            : (
              <>
              <p style={{ marginTop: 10 }}><St soort="serious">{t('{n} instelling(en) staan nog niet goed voor ADTS ZERO:', { n: d.changes.length })}</St></p>
              <table className="standaard" style={{ marginTop: 10 }}>
                <thead><tr><th>{t('Beleid')}</th><th>{t('Veld')}</th><th>{t('Nu')}</th><th>{t('Standaard')}</th></tr></thead>
                <tbody>{d.changes.map((w, i) => (
                  <tr key={i}>
                    <td className="mono" style={{ fontSize: 12 }}>{w.beleid}<br /><span className="muted">{apiObject[w.object]}</span></td>
                    <td>{label(w)}<br /><span className="mono muted" style={{ fontSize: 12 }}>{w.veld === 'aanmaken' ? 'POST' : w.veld}</span></td>
                    <td className="mono">{waarde(w.nu)}</td>
                    <td className="mono"><b>{waarde(w.nieuw)}</b></td>
                  </tr>
                ))}</tbody>
              </table>
              </>
            ))}
          {magUitrollen && !d.objections.length && d.changes.length > 0 && !d.pending.length && !actief && (bevestig ? (
            <div className="callout warn" style={{ marginTop: 12, display: 'grid', gap: 8 }}>
              <span>{t('ADTS ZERO wijzigt deze {n} velden op {fmc} en rolt uit naar: {firewalls}. Daarna kijkt ADTS ZERO of er nog syslog binnenkomt.', { n: d.changes.length, fmc: d.fmc.name, firewalls: d.devices.map((x) => x.name).join(', ') })}</span>
              {haPartners.length > 0 && <span className="soft">{t('HA-paar: {namen} gaan automatisch mee (gedeeld beleid).', { namen: haPartners.map((x) => x.name).join(', ') })}</span>}
              {nietHa.length > 0 && <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={ookAndere} onChange={(e) => zetOokAndere(e.target.checked)} />
                {t('Hetzelfde beleid geldt ook voor {namen}; die gaan mee', { namen: nietHa.map((x) => x.name).join(', ') })}</label>}
              {/* De FMC zegt pas na het wijzigen of de uitrol het verkeer onderbreekt (Snort-herstart). Zonder dit
                  vinkje rolt ZERO dan niet uit en zet het de velden terug. */}
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={onderbreking} onChange={(e) => zetOnderbreking(e.target.checked)} />
                {t('De uitrol mag het verkeer even onderbreken (als de FMC dat meldt, bijvoorbeeld een herstart van Snort)')}</label>
              <div className="actions" style={{ paddingTop: 0 }}>
                <button type="button" className="btn primary" disabled={bezig || (nietHa.length > 0 && !ookAndere)} onClick={rolUit}>{bezig ? t('Bezig…') : t('Nu uitrollen')}</button>
                <button type="button" className="btn" onClick={() => zetBevestig(false)}>{t('Annuleren')}</button>
              </div>
            </div>
          ) : <div className="actions"><button type="button" className="btn primary" onClick={() => zetBevestig(true)}>{t('Uitrollen…')}</button></div>)}
        </>
      )}

      {d?.available && magUitrollen && !actief && (toewijs ? (
        <div className="callout" style={{ marginTop: 14, display: 'grid', gap: 8 }}>
          <b>{t('Ander beleid toewijzen')}</b>
          <div className="two">
            <label className="field"><span>{t('Platformbeleid')}</span>
              <select value={toewijs.platformId ?? ''} onChange={(e) => zetToewijs({ ...toewijs, platformId: e.target.value })}>
                {!d.platform && <option value="">—</option>}
                {d.policies.platform.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
            <label className="field"><span>{t('Toegangsbeleid')}</span>
              <select value={toewijs.toegangId ?? ''} onChange={(e) => zetToewijs({ ...toewijs, toegangId: e.target.value })}>
                {d.policies.access.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
          </div>
          {toegangAnders && <div className="callout crit">{t('Een ander toegangsbeleid vervangt alle toegangsregels van {fw}: wat het nieuwe beleid niet toestaat, wordt geweigerd. Kijk eerst of het nieuwe beleid de regels heeft die nodig zijn.', { fw: bron.name })}</div>}
          {(platformAnders || toegangAnders) && (
            <>
              <span className="soft">{t('Hangt de firewall nu via een groep aan een beleid, dan hangt hij hierna rechtstreeks aan het nieuwe.')}</span>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={toewijs.onderbreking} onChange={(e) => zetToewijs({ ...toewijs, onderbreking: e.target.checked })} />
                {toegangAnders ? t('Het verkeer mag even onderbroken worden (verplicht bij een ander toegangsbeleid)') : t('De uitrol mag het verkeer even onderbreken (als de FMC dat meldt, bijvoorbeeld een herstart van Snort)')}</label>
              {/* In gebruik (stuurt syslog): alleen met een reden, bijvoorbeeld de change (besluit bestaande-toegangsbeleiden). */}
              {bron.status === 'actief' && <label className="field"><span>{t('Reden (deze firewall is in gebruik)')}</span>
                <input className="input" value={toewijs.reden} onChange={(e) => zetToewijs({ ...toewijs, reden: e.target.value })} placeholder={t('Bijvoorbeeld het nummer van de change')} /></label>}
              <label className="field"><span>{t('Typ {naam} om te bevestigen', { naam: bron.name })}</span>
                <input className="input mono" value={toewijs.bevestig} onChange={(e) => zetToewijs({ ...toewijs, bevestig: e.target.value })} autoComplete="off" /></label>
            </>
          )}
          <div className="actions" style={{ paddingTop: 0 }}>
            <button type="button" className="btn danger" disabled={bezig || !(platformAnders || toegangAnders) || toewijs.bevestig.trim() !== bron.name || (toegangAnders && !toewijs.onderbreking)
              || (bron.status === 'actief' && toewijs.reden.trim().length < 10)}
              onClick={wijsToe}>{bezig ? t('Bezig…') : t('Toewijzen en uitrollen')}</button>
            <button type="button" className="btn" onClick={() => zetToewijs(null)}>{t('Annuleren')}</button>
          </div>
        </div>
      ) : <div><button type="button" className="namelink" style={{ marginTop: 10 }}
        onClick={() => zetToewijs({ platformId: d.platform?.id ?? '', toegangId: d.access?.id ?? '', bevestig: '', onderbreking: false, reden: '' })}>{t('Ander beleid toewijzen…')}</button></div>)}

      {bron.syslogRollouts.length > 0 && (
        <div className="rows" style={{ marginTop: 14 }}>
          {bron.syslogRollouts.map((r) => (
            <div key={r.id} className={`row${BEZIG.has(r.status) ? ' bezig' : ''}`} style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}>
              <span><b>{r.kind === 'uitrollen' ? t('Uitrol {n}', { n: r.id }) : r.kind === 'toewijzen' ? t('Toewijzen {n}', { n: r.id }) : t('Terugdraaien van uitrol {n}', { n: r.undoOf })}</b>
                <span className="muted"> · {wanneer(r.startedAt)}{r.by && <> · {r.by.name || r.by.number}</>} · {r.devices.map((x) => x.name).join(', ')}</span>
                {r.error && <><br /><span className="soft">{r.error}</span></>}
                <br /><span className="muted" style={{ fontSize: 12 }}>{r.log.map((s) => t(s.key, s.params)).join(' → ')}{BEZIG.has(r.status) ? ' →…' : ''}</span>
                {BEZIG.has(r.status) && UITLEG[r.status] && <><br /><span className="soft" style={{ fontSize: 12 }}>{t(UITLEG[r.status])}</span></>}</span>
              <span style={{ textAlign: 'right' }}><span title={UITLEG[r.status] ? t(UITLEG[r.status]) : undefined}><St soort={SOORT[r.status]} icoon={BEZIG.has(r.status) ? <Spinner /> : undefined}>{t(STAND[r.status])}{BEZIG.has(r.status) ? '…' : ''}</St></span>
                {magUitrollen && r.kind !== 'terugdraaien' && (r.applied ?? []).length > 0 && !['wacht', 'uitrollen', 'nakijken'].includes(r.status) && !actief
                  && !bron.syslogRollouts.some((x) => x.undoOf === r.id && !['gestopt', 'mislukt'].includes(x.status))
                  && <><br /><button type="button" className="namelink" disabled={bezig} onClick={() => terug(r.id)}>{t('Terugdraaien')}</button></>}</span>
            </div>
          ))}
        </div>
      )}
      <p className="noot">{t('Alleen de velden in de tabel — de standaardactie (implicit deny) en de block-regels naar syslog, zodat elke deny binnenkomt; de allow- en trust-regels, de andere syslogservers, de buffer en rate limits blijven zoals ze zijn.')}</p>
    </section>
  );
}
